import { useAnswerHistory, useDebounce } from "@hooks";
import { ChevronRight } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Button, Tooltip } from "@mui/material";
import { buildAutoSuggestPromptFromElement, isStepComplete } from "@utils";
import { isEqual } from "lodash";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MultiStepFormStepErrorBoundary } from "src/components/ErrorBoundary";
import { StepHeader } from "src/components/Form/StepHeader";
import { useSaveAnswer } from "src/components/FormStep/utils/useSaveAnswer";
import {
  useDataKeyApplyListener,
  usePromptInjection,
} from "src/hooks/useToolActions";
import { useStore } from "src/stores";
import { getDocumentNavigationUrl } from "src/utils/navigation";
import {
  Device,
  Document,
  DocumentVersion,
  RoadmapTasks,
  User,
} from "../../stores/models";
import { StepValue, TemplateConfig, TemplateQuestion } from "../../types";
import { ElementMapper } from "../Form/ElementMapper";
import { getAnswer, getContextConfig, runTransformer } from "../Form/utils";

type Props = {
  activeStep: TemplateQuestion;
  steps: TemplateQuestion[];
  step: TemplateQuestion;
  containerRef: React.RefObject<HTMLDivElement>;
  device: Device;
  documentVersion: DocumentVersion;
  documents: Document[];
  user: User;
  templatesConfig: TemplateConfig;
  tasks: RoadmapTasks;
};

export const MultiStepFormStep = observer(
  ({
    activeStep,
    device,
    documents,
    documentVersion,
    step,
    steps,
    user,
    templatesConfig,
    tasks,
  }: Props) => {
    const {
      orgId = "",
      deviceId = "",
      docId = "",
    } = useParams<{
      orgId: string;
      deviceId: string;
      docId?: string;
    }>();

    let [_, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const { uiStore, agentStore } = useStore();

    const isVisible = activeStep.id === step.id;
    const isLastStep = activeStep.id === steps[steps.length - 1].id;
    const isAnswerStringElement =
      step.element.type !== "answerItemsElement" &&
      step.element.type !== "fileUpload";

    const { saveAnswer } = useSaveAnswer(orgId, deviceId);

    const { injectPrompt } = usePromptInjection();

    const { debounce, cancelDebounce } = useDebounce(
      2000,
      (value: StepValue) => {
        if (isAnswerStringElement) {
          saveAnswer(docId, value, step, documentVersion);
        }
      }
    );

    const [isGenerating, setIsGenerating] = useState(false);

    const [stepValue, setStepValue] = useState<StepValue>(
      getAnswer({
        step,
        documentVersion,
        documents,
        templatesConfig,
      })
    );

    const savedStepValue = useMemo(
      () =>
        getAnswer({
          step,
          documentVersion,
          documents,
          templatesConfig,
        }),
      [step, documentVersion, documents, templatesConfig]
    );

    // Sync local state when saved answer changes from external source
    const previousSavedStepValueRef = useRef<StepValue | null>(null);

    useEffect(() => {
      if (!isVisible) {
        return;
      }

      const previousSavedStepValue = previousSavedStepValueRef.current;
      const hasSavedChanged =
        !previousSavedStepValue ||
        !isEqual(previousSavedStepValue, savedStepValue);

      if (hasSavedChanged && !isEqual(stepValue, savedStepValue)) {
        setStepValue(savedStepValue);
      }

      previousSavedStepValueRef.current = savedStepValue;
    }, [isVisible, savedStepValue, stepValue]);

    const handleForceRunTransformer = () => {
      const newAnswer = runTransformer({
        step,
        documentVersion,
        documents,
        templatesConfig,
      });
      setStepValue({ answer: newAnswer, answerFileId: undefined });
    };

    const nextStep = (
      step: TemplateQuestion,
      answer: StepValue,
      documentVersion: DocumentVersion,
      steps: TemplateQuestion[]
    ) => {
      cancelDebounce();

      const savedAnswer = answer;

      saveAnswer(docId, savedAnswer, step, documentVersion);
      setStepValue(savedAnswer);

      const nextStep = steps[steps.findIndex((s) => s.id === step.id) + 1];
      setSearchParams((params) => ({ ...params, step: nextStep.id }));
      // Cancel the debounce to make sure it is not conflicting with the next step or any network requests that might be happening
    };

    const [updateHistory, undo, redo] = useAnswerHistory({
      elementId: step.id,
    });

    const handleChange = (_: TemplateQuestion, value: StepValue) => {
      setStepValue(value);
      debounce(value);
    };

    // Listen for tool actions that want to apply values to this step
    const handleApplyFromTool = (value: string) => {
      setStepValue({
        answer: value,
        answerFileId: undefined,
        answerItems: undefined,
      });
    };

    useDataKeyApplyListener(step.id, handleApplyFromTool, {});

    const handleUndo = () => {
      const newAnswer = undo();
      if (newAnswer) {
        handleChange(step, { answer: newAnswer });
      }
    };

    const handleRedo = () => {
      const newAnswer = redo();
      if (newAnswer) {
        handleChange(step, { answer: newAnswer });
      }
    };

    const handleAutoSuggest = async () => {
      const contextConfig = getContextConfig(
        step,
        documents,
        templatesConfig,
        device
      );
      const fullPrompt = buildAutoSuggestPromptFromElement(
        step,
        contextConfig,
        device
      );

      const preview = `Auto-suggest for: ${step.element.options.label}`;

      agentStore.navigateToThread(null);
      uiStore.setIsRightPanelOpen(true);

      injectPrompt({
        prompt: fullPrompt,
        preview: preview,
        source: `auto-suggest-${step.id}`,
        submit: false,
        replaceInput: true,
        contextConfig: contextConfig,
        device: device,
        documents: documents,
        user: user,
        deviceId: deviceId,
        templatesConfig: templatesConfig,
      });
    };

    useEffect(() => {
      if (isAnswerStringElement) {
        updateHistory(stepValue.answer);
      }
    }, [stepValue]);

    const handleNextStep = () => {
      nextStep(step, stepValue, documentVersion, steps);
    };

    const hasUnsavedAnswerItems =
      step.element.type === "answerItemsElement" &&
      (stepValue.answerItems || []).some(
        (item) =>
          !item.archived &&
          (item.createdBy === "" || item.createdBy === "editing")
      );

    const isNextStepDisabled =
      !isStepComplete(step, stepValue) || isGenerating || hasUnsavedAnswerItems;
    const disabledNextStepTooltip = hasUnsavedAnswerItems
      ? "Save or remove unsaved list items above to proceed"
      : "";

    const handleFinalStep = async (
      step: TemplateQuestion,
      answer: StepValue,
      docVersion: DocumentVersion
    ) => {
      setIsGenerating(true);

      // Cancel the debounce to make sure it is not conflicting with the next step or any network requests that might be happening
      cancelDebounce();

      const savedAnswer = await saveAnswer(docId, answer, step, docVersion);

      // TODO move this to onMutate or onSuccess of the saveAnswerMutation
      if (savedAnswer && "element" in savedAnswer) {
        // Replace the answer in the document version with the saved answer
        docVersion.answers = docVersion.answers.map((a) =>
          a.element === step.id ? savedAnswer : a
        );

        // Or add the answer if it does not exist yet in the document version add the answer
        if (!docVersion.answers.find((a) => a.element === step.id)) {
          docVersion.answers.push(savedAnswer);
        }
      }

      navigate(
        getDocumentNavigationUrl(orgId, deviceId, docId, docVersion.id),
        { flushSync: true }
      );
    };

    if (!isVisible) {
      return null;
    }

    const contextConfig = getContextConfig(
      step,
      documents,
      templatesConfig,
      device
    );

    return (
      <MultiStepFormStepErrorBoundary>
        {/* replacing div with slider for now since it caused performance issues */}
        <div key={step.id} className={`w-full flex flex-1 flex-col gap-y-2`}>
          <StepHeader templateElement={step} documents={documents} />
          <div className="flex w-full flex-1 flex-row items-start gap-4">
            <div className={`flex h-full flex-1 flex-col gap-y-4 w-full`}>
              <ElementMapper
                step={step}
                templatesConfig={templatesConfig}
                onChange={handleChange}
                stepValue={stepValue}
                onReRunTransformer={handleForceRunTransformer}
                onRedo={handleRedo}
                onUndo={handleUndo}
                documentVersion={documentVersion}
                contextConfig={contextConfig}
                deviceId={deviceId}
                tasks={tasks}
                onAutoSuggest={handleAutoSuggest}
              />
              <div className="self-end">
                <div className="flex gap-2">
                  {!isLastStep && (
                    <Tooltip
                      title={disabledNextStepTooltip}
                      disableHoverListener={!hasUnsavedAnswerItems}
                      disableFocusListener={!hasUnsavedAnswerItems}
                    >
                      <span>
                        <Button
                          onClick={handleNextStep}
                          disabled={isNextStepDisabled}
                          variant="contained"
                          endIcon={<ChevronRight />}
                        >
                          Next
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {isLastStep && (
                    <>
                      <Tooltip
                        title={disabledNextStepTooltip}
                        disableHoverListener={!hasUnsavedAnswerItems}
                        disableFocusListener={!hasUnsavedAnswerItems}
                      >
                        <span>
                          <LoadingButton
                            variant="contained"
                            loading={isGenerating}
                            disabled={isNextStepDisabled}
                            onClick={() =>
                              handleFinalStep(step, stepValue, documentVersion)
                            }
                          >
                            Generate
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MultiStepFormStepErrorBoundary>
    );
  }
);
